import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { LEGAL_DOCS } from "@/data/legal";

export default function Legal({ doc }) {
  const data = LEGAL_DOCS[doc];

  useEffect(() => {
    if (data) document.title = `${data.title} — Qauntum International`;
    return () => {
      document.title = "Qauntum International";
    };
  }, [data]);

  if (!data) return <Navigate to="/" replace />;

  return (
    <>
      <Header />
      <main className="px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-24 md:pb-32" data-testid={`legal-page-${data.slug}`}>
        <Reveal className="max-w-3xl">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-quantum-muted hover:text-quantum-gold transition-colors mb-10"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-quantum-gold mb-6">{data.eyebrow}</p>
          <h1 className="font-serif-q text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-light text-quantum-text mb-6">
            {data.title}
          </h1>
          <p className="text-quantum-muted leading-relaxed mb-2">{data.intro}</p>
          <p className="text-xs uppercase tracking-[0.2em] text-quantum-muted/70" data-testid="legal-updated">
            Last updated: {data.updated}
          </p>
        </Reveal>

        <div className="max-w-3xl mt-16 md:mt-20 space-y-12 md:space-y-14" data-testid="legal-content">
          {data.sections.map((s, i) => (
            <Reveal key={s.heading} delay={Math.min(i * 0.05, 0.3)}>
              <section data-testid={`legal-section-${i}`}>
                <h2 className="font-serif-q text-2xl md:text-3xl text-quantum-text mb-4 leading-snug">
                  <span className="text-quantum-gold mr-3">{String(i + 1).padStart(2, "0")}</span>
                  {s.heading}
                </h2>
                <div className="space-y-4 pl-0 md:pl-12">
                  {s.body.map((p, j) => (
                    <p key={j} className="text-quantum-muted leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal className="max-w-3xl mt-20 pt-10 border-t border-white/10">
          <div className="flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.2em]" data-testid="legal-crosslinks">
            <span className="text-quantum-muted/70">Related:</span>
            {Object.values(LEGAL_DOCS)
              .filter((d) => d.slug !== data.slug)
              .map((d) => (
                <Link
                  key={d.slug}
                  to={`/${d.slug}`}
                  data-testid={`legal-link-${d.slug}`}
                  className="text-quantum-muted hover:text-quantum-gold transition-colors"
                >
                  {d.title}
                </Link>
              ))}
          </div>
        </Reveal>
      </main>
      <Footer />
    </>
  );
}
